import * as React from 'react'

// RPD-5169 (EIS TPRM audit): 15-minute inactive session timeout with a
// warning at 12 minutes. Client-side enforcement per the requirement.
export const IDLE_TIMEOUT_MS = 15 * 60 * 1000
export const IDLE_WARNING_AT_MS = 12 * 60 * 1000

// localStorage keeps the stamp shared across tabs: activity in any tab keeps
// every tab alive, and the timeout signs out all of them together.
const LAST_ACTIVITY_KEY = 'sato-last-activity'
export const SESSION_EXPIRED_FLAG = 'sato-session-expired'

// Stamp writes are throttled; enforcement below always compares wall clock
// against the stored stamp, so a missed write can only expire us early by
// this margin, never late.
const STAMP_THROTTLE_MS = 5 * 1000
const CHECK_INTERVAL_MS = 1000

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
    'mousemove',
    'mousedown',
    'keydown',
    'touchstart',
    'scroll',
]

const readLastActivity = (): number => {
    const raw = localStorage.getItem(LAST_ACTIVITY_KEY)
    const parsed = raw ? parseInt(raw, 10) : NaN
    return Number.isFinite(parsed) ? parsed : 0
}

const stampActivity = () => {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()))
}

export interface IIdleSessionState {
    is_warning: boolean
    seconds_left: number
    staySignedIn: () => void
}

// Idle watcher for an authenticated session. Arm only while a user is signed
// in; on timeout it sets the expiry flag (read by the login page) and calls
// the provided sign-out. Pre-warning, any qualifying interaction resets the
// timer; once the warning is shown, dismissal requires the explicit
// [Stay signed in] click (or activity in another tab).
export const useIdleSession = (
    armed: boolean,
    onTimeout: () => void
): IIdleSessionState => {
    const { useCallback, useEffect, useRef, useState } = React
    const [is_warning, setIsWarning] = useState(false)
    const [seconds_left, setSecondsLeft] = useState(0)
    const is_warning_ref = useRef(false)
    const timed_out_ref = useRef(false)

    const staySignedIn = useCallback(() => {
        stampActivity()
        is_warning_ref.current = false
        setIsWarning(false)
    }, [])

    useEffect(() => {
        if (!armed) {
            is_warning_ref.current = false
            timed_out_ref.current = false
            setIsWarning(false)
            return undefined
        }

        // A fresh session starts its idle clock now (also prevents a stale
        // stamp from a previous session expiring us at first tick).
        stampActivity()

        let last_stamp_write = Date.now()
        const onActivity = () => {
            // Once the warning is up, dismissing it requires the explicit
            // [Stay signed in] click (product decision, RPD-5169) — stray
            // mouse movement must not silently extend a flagged-idle session.
            // Activity in ANOTHER tab still stands the warning down (the
            // check() else-branch sees the fresh cross-tab stamp).
            if (is_warning_ref.current) return
            const now = Date.now()
            if (now - last_stamp_write < STAMP_THROTTLE_MS) return
            last_stamp_write = now
            stampActivity()
        }

        const check = () => {
            if (timed_out_ref.current) return
            const idle_for = Date.now() - readLastActivity()

            if (idle_for >= IDLE_TIMEOUT_MS) {
                timed_out_ref.current = true
                is_warning_ref.current = false
                setIsWarning(false)
                sessionStorage.setItem(SESSION_EXPIRED_FLAG, '1')
                onTimeout()
                return
            }

            if (idle_for >= IDLE_WARNING_AT_MS) {
                is_warning_ref.current = true
                setIsWarning(true)
                setSecondsLeft(
                    Math.max(0, Math.ceil((IDLE_TIMEOUT_MS - idle_for) / 1000))
                )
            } else if (is_warning_ref.current) {
                // Stamp went fresh (Stay signed in, or activity in another
                // tab) — stand down.
                is_warning_ref.current = false
                setIsWarning(false)
            }
        }

        ACTIVITY_EVENTS.forEach((event) =>
            window.addEventListener(event, onActivity, { passive: true })
        )
        const interval = window.setInterval(check, CHECK_INTERVAL_MS)
        // Interval timers pause on some platforms while the machine sleeps —
        // re-check the wall clock the moment the tab becomes visible again.
        document.addEventListener('visibilitychange', check)

        return () => {
            ACTIVITY_EVENTS.forEach((event) =>
                window.removeEventListener(event, onActivity)
            )
            window.clearInterval(interval)
            document.removeEventListener('visibilitychange', check)
        }
    }, [armed, onTimeout])

    return { is_warning, seconds_left, staySignedIn }
}
